import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, AlertCircle, Shield, CheckCircle2 } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Forgot password flow
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState(null);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/forgot-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: forgotEmail }),
        }
      );
      if (!response.ok) throw new Error('Request failed');
      setForgotSent(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotError("We couldn't send the reset email. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        }
      );

      if (!response.ok) {
        // Surface lockout (429) message from backend, otherwise generic
        if (response.status === 429) {
          const data = await response.json().catch(() => ({}));
          setError(data.detail || 'Too many failed attempts. Please try again later.');
        } else {
          setError('Invalid email or password');
        }
        return;
      }

      const data = await response.json();

      // Store session token and user data
      localStorage.setItem('session_token', data.session_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Store in cookie as well (samesite=lax so cross-site links don't
      // strip it — matches App.js cookie config)
      document.cookie = `session_token=${data.session_token}; path=/; max-age=${7*24*60*60}; samesite=lax; secure`;

      // Redirect to admin dashboard
      navigate('/admin', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink font-body flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-brand-surface border border-brand-border shadow-[0_8px_30px_rgb(0,0,0,0.06)] overflow-hidden">
          {/* Header */}
          <div className="bg-brand-ink text-white p-8 text-center">
            <div className="border border-white/20 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <Shield className="h-10 w-10" />
            </div>
            <h1 className="font-display text-3xl">Admin</h1>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-sage mt-2">ReflectiveMind Administration</p>
          </div>

          {/* Form */}
          {!showForgot ? (
          <form onSubmit={handleLogin} className="p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-brand-sage mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-brand-ink-muted" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-7 pr-4 py-2.5 bg-transparent border-0 border-b border-brand-border focus:border-brand-accent outline-none rounded-none text-brand-ink placeholder:text-brand-ink-muted/50"
                    placeholder="admin@reflective-mind.org"
                    data-testid="admin-login-email"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-sage">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgot(true);
                      setForgotEmail(email || '');
                      setForgotSent(false);
                      setForgotError(null);
                    }}
                    className="text-xs text-brand-accent hover:text-brand-cta transition-colors"
                    data-testid="admin-forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-brand-ink-muted" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-7 pr-4 py-2.5 bg-transparent border-0 border-b border-brand-border focus:border-brand-accent outline-none rounded-none text-brand-ink placeholder:text-brand-ink-muted/50"
                    placeholder="••••••••"
                    data-testid="admin-login-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-cta hover:bg-brand-cta-hover text-white py-3 px-6 font-medium transition-all shadow-[0_4px_14px_rgba(5,112,222,0.35)] hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(5,112,222,0.25)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed "
                data-testid="admin-login-submit"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </div>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-xs uppercase tracking-[0.2em] text-brand-sage hover:text-brand-ink transition-colors"
              >
                ← Back to Home
              </button>
            </div>
          </form>
          ) : (
          <form onSubmit={handleForgot} className="p-8" data-testid="admin-forgot-password-form">
            {!forgotSent ? (
              <>
                <h2 className="font-display text-2xl text-brand-ink mb-2">Reset admin password</h2>
                <p className="text-sm text-brand-ink-muted mb-6">
                  Enter the admin email address. We'll send a one-time reset link that expires in 60 minutes.
                </p>

                {forgotError && (
                  <div className="mb-5 p-3 bg-red-50 border border-red-200 flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <p className="text-sm text-red-700">{forgotError}</p>
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-sage mb-2">
                    Admin email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-brand-ink-muted" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="w-full pl-7 pr-4 py-2.5 bg-transparent border-0 border-b border-brand-border focus:border-brand-accent outline-none rounded-none text-brand-ink placeholder:text-brand-ink-muted/50"
                      placeholder="admin@reflective-mind.org"
                      data-testid="admin-forgot-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-brand-cta hover:bg-brand-cta-hover text-white py-3 px-6 font-medium transition-all shadow-[0_4px_14px_rgba(5,112,222,0.35)] hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(5,112,222,0.25)] disabled:opacity-50 "
                  data-testid="admin-forgot-submit"
                >
                  {forgotLoading ? 'Sending…' : 'Send reset link'}
                </button>
              </>
            ) : (
              <div className="text-center" data-testid="admin-forgot-sent-message">
                <div className="inline-flex items-center justify-center bg-brand-sage/15 border border-brand-sage/30 p-4 mb-5">
                  <CheckCircle2 className="h-8 w-8 text-brand-sage" />
                </div>
                <h2 className="font-display text-2xl text-brand-ink mb-3">Check your inbox</h2>
                <p className="text-sm text-brand-ink-muted">
                  If <strong className="text-brand-ink">{forgotEmail}</strong> matches the admin account, a reset link is on its way.
                  The link expires in <strong className="text-brand-ink">60 minutes</strong> and can only be used once.
                </p>
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => {
                  setShowForgot(false);
                  setForgotSent(false);
                  setForgotError(null);
                }}
                className="text-xs uppercase tracking-[0.2em] text-brand-sage hover:text-brand-ink transition-colors"
                data-testid="admin-forgot-back-link"
              >
                ← Back to login
              </button>
            </div>
          </form>
          )}
        </div>

        <p className="text-center text-xs uppercase tracking-[0.2em] text-brand-sage mt-6">
          Admin access only · Unauthorised attempts are logged
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
